import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@athena/ui";
import type { ClientDoNotContactReason } from "@athena/common/graphql/generated/schema";
import { useSetDoNotContact } from "@/hooks/useOutreachStatus";
import { fullName, type ClientRecord } from "../helpers/client";

/** The choice for a client who is not do not contact: Radix Select has no unset value, only a chosen one. */
const NOT_DO_NOT_CONTACT = "NONE";

/** In the order the dropdown offers them. */
const DO_NOT_CONTACT_REASONS = [
    "CLIENT_REQUESTED",
    "STAFF_REQUESTED",
    "READMITTED",
    "DECEASED",
] as const satisfies readonly ClientDoNotContactReason[];

const TRIGGER_ID = "do-not-contact-reason";

/**
 * Whether a client is do not contact, and why, as one dropdown beside the profile's status badge
 * (ATH-231). Picking a reason marks them; "None" puts them back in outreach.
 *
 * Readmitted is shown but cannot be picked: the KIPU sync sets it while they are back in treatment
 * and clears it when they are discharged. The profile explains that under the header.
 */
export function DoNotContactSelect({ client }: { client: ClientRecord }) {
    const { t } = useTranslation();
    const setDoNotContact = useSetDoNotContact();
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);

    const marked = client.status === "DO_NOT_CONTACT";
    const value = marked && client.doNotContactReason ? client.doNotContactReason : NOT_DO_NOT_CONTACT;

    const change = async (next: string) => {
        setSaving(true);
        try {
            await setDoNotContact(
                client.id,
                fullName(client),
                next === NOT_DO_NOT_CONTACT ? null : (next as ClientDoNotContactReason),
            );
        } catch {
            // The hook has said what went wrong; the dropdown still shows what is saved.
        } finally {
            setSaving(false);
        }
    };

    return (
        // One pill beside the status badge: the label, a hairline, and the choice.
        <div className="border-border bg-card focus-within:ring-ring inline-flex h-8 items-center rounded-full border pl-3 focus-within:ring-2">
            <Label htmlFor={TRIGGER_ID} className="text-muted-foreground text-xs font-medium">
                {/* Beside a Do not contact badge, "Reason" says enough; beside any other, it would not. */}
                {t(marked ? "admin.alumni.doNotContact.label" : "admin.alumni.doNotContact.labelNotMarked")}
            </Label>
            <span className="bg-border mx-2 h-4 w-px" aria-hidden />
            <Select value={value} onValueChange={change} disabled={saving}>
                <SelectTrigger
                    id={TRIGGER_ID}
                    className="text-text-heading h-full w-auto gap-1.5 rounded-full border-0 bg-transparent py-0 pr-3 pl-0 text-xs font-medium focus:ring-0 focus:ring-offset-0"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={NOT_DO_NOT_CONTACT}>{t("admin.alumni.doNotContact.none")}</SelectItem>
                    {DO_NOT_CONTACT_REASONS.map((reason) => (
                        <SelectItem key={reason} value={reason} disabled={reason === "READMITTED"}>
                            {t(`admin.alumni.doNotContact.reason.${reason}`)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
